import { api } from './apiClient';

/**
 * Recurring Journal Control Center service. Mounted at /recurring (see
 * app.js). The apiClient auto-unwraps the { status, data } envelope, so each
 * call returns `data` directly.
 */

export interface RecurringDashboard {
  asOf: string;
  activeTemplates: number;
  dueThisWeek: number;
  dueThisMonth: number;
  postedThisMonth: { count: number; value: number };
  pendingApproval: number;
  failed: number;
  totalScheduledValue: number;
  nextPostingDate: string | null;
}

export interface RecurringRow {
  id: number;
  name: string;
  template_id: number;
  template_name?: string;
  template_category?: string;
  frequency: string;
  start_date: string;
  end_date: string | null;
  next_run_date: string | null;
  last_run_date: string | null;
  generated_count: number;
  next_amount: number | string;
  status: string;
  posting_mode: string;
  approval_status: string;
  last_error: string | null;
  last_error_at: string | null;
}

export interface RecurringDetail {
  header: RecurringRow & Record<string, any>;
  lines: any[];
  runs: any[];
}

export interface RecurringListFilters {
  status?: string; frequency?: string; postingMode?: string;
  approvalStatus?: string; search?: string;
}

export interface RecurringCreateInput {
  name: string;
  templateId: number;
  frequency: string;
  startDate: string;
  endDate?: string | null;
  postingMode: string;
  /** Optional "end after N occurrences" (migration 065). */
  maxOccurrences?: number | null;
  description?: string | null;
}

const qs = (f?: Record<string, any>) => {
  if (!f) return '';
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.append(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const recurringService = {
  dashboard: (): Promise<RecurringDashboard> => api.get('/recurring/dashboard'),
  list: (filters?: RecurringListFilters): Promise<{ recurring: RecurringRow[] }> => api.get(`/recurring${qs(filters)}`),
  detail: (id: number): Promise<RecurringDetail> => api.get(`/recurring/${id}`),
  templates: (): Promise<{ templates: any[] }> => api.get('/recurring/templates'),

  create: (d: RecurringCreateInput): Promise<{ recurring: any }> => api.post('/recurring', d),
  update: (id: number, d: any): Promise<RecurringDetail> => api.patch(`/recurring/${id}`, d),
  pause: (id: number): Promise<RecurringDetail> => api.post(`/recurring/${id}/pause`, {}),
  resume: (id: number): Promise<RecurringDetail> => api.post(`/recurring/${id}/resume`, {}),
  approve: (id: number): Promise<RecurringDetail> => api.post(`/recurring/${id}/approve`, {}),
  generate: (id: number): Promise<any> => api.post(`/recurring/${id}/generate`, {}),
  runDue: (): Promise<{ generated: number; failed: number; results: any[]; failures: any[] }> => api.post('/recurring/run-due', {}),
};

export default recurringService;
